import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Save, Bell, Award, Layout } from 'lucide-react';
import AiThemeHelper from './components/AiThemeHelper';
import ThemeFineTuneControls from '@/components/sandbox/ThemeFineTuneControls';
import api from '@/lib/api';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Card from '@/components/ui/Card';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { cn } from '@/lib/utils';
import { ALL_PRESET_IDS, LOOK_LABELS } from '@/looks';
import type { DesignThemeConfig } from '@/stores/design-preview-store';

interface ValueProp {
  icon: string;
  title: string;
  description: string;
}

const ICON_OPTIONS = [
  { label: 'Leaf', value: 'Leaf' },
  { label: 'Truck', value: 'Truck' },
  { label: 'Award', value: 'Award' },
  { label: 'Sprout', value: 'Sprout' },
  { label: 'Shield', value: 'ShieldCheck' },
  { label: 'Heart', value: 'Heart' },
  { label: 'Mail', value: 'Mail' },
];

export default function ThemeCustomizer() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Defaults come from the tenant's site.config.ts (injected at build time
  // by vite.config.ts). Falling back to empty strings produces a blank color
  // input rather than an incorrect tenant-A color leaking into tenant B.
  const [themeConfig, setThemeConfig] = useState<DesignThemeConfig>({
    primary_color: import.meta.env.VITE_THEME_PRIMARY_700 || '',
    accent_color: import.meta.env.VITE_THEME_ACCENT_BASE || '',
    bg_warm: import.meta.env.VITE_THEME_BG_WARM || '',
    font_heading: import.meta.env.VITE_THEME_FONT_DISPLAY || 'Playfair Display',
    font_body: import.meta.env.VITE_THEME_FONT_SANS || 'Inter',
  });

  const [showAiHelper, setShowAiHelper] = useState(false);

  const [announcementText, setAnnouncementText] = useState('');
  const [announcementEnabled, setAnnouncementEnabled] = useState(true);

  const [valueProps, setValueProps] = useState<ValueProp[]>([
    { icon: 'Award', title: 'Quality Products', description: 'Premium quality in everything we offer' },
    { icon: 'Truck', title: 'Fast Delivery', description: 'Quick and reliable delivery' },
    { icon: 'Sprout', title: 'Expert Support', description: 'Our team is here to help' },
    { icon: 'ShieldCheck', title: 'Satisfaction Guaranteed', description: '100% satisfaction or your money back' },
  ]);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get<{ settings: Record<string, any> }>('/admin/settings');
      const settings = response.settings || {};

      if (settings.theme_config) setThemeConfig(settings.theme_config);
      setAnnouncementText(settings.announcement_bar_text || '');
      setAnnouncementEnabled(settings.announcement_bar_enabled !== false);
      if (Array.isArray(settings.value_propositions)) setValueProps(settings.value_propositions);
    } catch {
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await api.put('/admin/settings', {
        settings: {
          theme_config: themeConfig,
          announcement_bar_text: announcementText,
          announcement_bar_enabled: announcementEnabled,
          value_propositions: valueProps,
        },
      });
      setSuccess('Theme settings saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch {
      setError('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  function updateValueProp(index: number, field: string, value: string) {
    const newProps = [...valueProps];
    newProps[index] = { ...newProps[index], [field]: value };
    setValueProps(newProps);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" role="status">
        <LoadingSpinner />
        <span className="sr-only">Loading theme settings...</span>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Theme Customizer</h1>
          <p className="text-gray-600 mt-1">Customize your store's visual appearance</p>
        </div>
        <Button variant="primary" onClick={handleSave} isLoading={saving}>
          <Save className="h-4 w-4 mr-2" aria-hidden="true" />
          Save Changes
        </Button>
      </div>

      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-800 text-sm">
        Want to try a new look on your live storefront before committing? Use the{' '}
        <Link to="/admin/settings/sandbox" className="font-medium underline">Design Sandbox</Link> to
        preview templates safely, then publish.
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" role="alert">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm" role="status">
          {success}
        </div>
      )}

      <div className="space-y-6">
        {/* Look — pick a curated design preset (header + hero + footer +
            typography + spacing all coordinated). The eight built-in Looks
            are starting points; the colors/typography below let you tweak. */}
        <Card padding="lg">
          <div className="flex items-center gap-2 mb-4">
            <Layout className="h-5 w-5 text-gray-500" aria-hidden="true" />
            <h2 className="text-lg font-semibold text-gray-900">Look</h2>
            <span className="text-xs text-gray-500">pick a starting style</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {ALL_PRESET_IDS.map((id) => {
              const isSelected = themeConfig.look?.preset_id === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setThemeConfig({ ...themeConfig, look: { preset_id: id } })}
                  className={cn(
                    'text-left p-3 rounded-lg border-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500',
                    isSelected
                      ? 'border-green-600 bg-green-50'
                      : 'border-gray-200 hover:border-gray-300 bg-white',
                  )}
                  aria-pressed={isSelected}
                >
                  <div className="font-medium text-gray-900 text-sm mb-1">
                    {LOOK_LABELS[id].label}
                  </div>
                  <div className="text-xs text-gray-600 leading-snug">
                    {LOOK_LABELS[id].description}
                  </div>
                </button>
              );
            })}
          </div>
        </Card>

        {/* Colors, Fonts, and advanced Look overrides (shared with the
            Design Sandbox so the two surfaces never drift). */}
        <ThemeFineTuneControls
          value={themeConfig}
          onChange={setThemeConfig}
          showAiButton
          onGenerateAiClick={() => setShowAiHelper(true)}
        />

        {/* Announcement Bar */}
        <Card padding="lg">
          <div className="flex items-center gap-2 mb-4">
            <Bell className="h-5 w-5 text-gray-500" aria-hidden="true" />
            <h2 className="text-lg font-semibold text-gray-900">Announcement Bar</h2>
          </div>
          <div className="space-y-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={announcementEnabled}
                onChange={(e) => setAnnouncementEnabled(e.target.checked)}
                className="h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-2 focus:ring-green-500"
              />
              <span className="text-sm font-medium text-gray-900">Show Announcement Bar</span>
            </label>
            <Input
              label="Announcement Text"
              value={announcementText}
              onChange={(e) => setAnnouncementText(e.target.value)}
              placeholder="Free shipping on orders over $50"
            />
          </div>
        </Card>

        {/* Value Propositions */}
        <Card padding="lg">
          <div className="flex items-center gap-2 mb-4">
            <Award className="h-5 w-5 text-gray-500" aria-hidden="true" />
            <h2 className="text-lg font-semibold text-gray-900">Value Propositions</h2>
            <span className="text-xs text-gray-500">(shown in pre-footer)</span>
          </div>
          <div className="space-y-4">
            {valueProps.map((prop, i) => (
              <div key={i} className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end p-3 bg-gray-50 rounded-lg">
                <Select
                  label="Icon"
                  value={prop.icon}
                  onChange={(e) => updateValueProp(i, 'icon', e.target.value)}
                  options={ICON_OPTIONS}
                />
                <Input
                  label="Title"
                  value={prop.title}
                  onChange={(e) => updateValueProp(i, 'title', e.target.value)}
                />
                <div className="sm:col-span-2">
                  <Input
                    label="Description"
                    value={prop.description}
                    onChange={(e) => updateValueProp(i, 'description', e.target.value)}
                  />
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {showAiHelper && (
        <div className="fixed inset-y-0 right-0 w-[420px] bg-white shadow-2xl z-50 border-l">
          <AiThemeHelper
            onApply={(palette, fonts) => {
              setThemeConfig(prev => ({
                ...prev,
                primary_color: palette.primary?.['500'] || prev.primary_color,
                accent_color: palette.accent?.base || prev.accent_color,
                bg_warm: palette.warm?.cream || prev.bg_warm,
                ...(fonts?.heading && { font_heading: fonts.heading }),
                ...(fonts?.body && { font_body: fonts.body }),
              }));
              setShowAiHelper(false);
            }}
            onClose={() => setShowAiHelper(false)}
          />
        </div>
      )}
    </div>
  );
}
